import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Message from './../components/Message';
import Loader from './../components/Loader';
import Paginate from './../components/Paginate';
import { 
  useGetBagsQuery, 
  useDeleteBagMutation, 
  useCreateBagMutation 
} from './../slices/bagsApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffNote = () => {
  const { pageNumber } = useParams();
  const { data, isLoading, error, refetch } = useGetBagsQuery({ pageNumber });
  const [deleteBag, { isLoading: loadingDelete }] = useDeleteBagMutation();
  const [createBag, { isLoading: loadingCreate }] = useCreateBagMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // NEW: Date range state for filtering total bags
  // Default start date is set to a fixed value (adjust as needed),
  // and default end date is today.
  const [date1, setDate1] = useState('2023-01-01');
  const [date2, setDate2] = useState(new Date().toISOString().split('T')[0]);

  // Delete bag handler
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this bag?')) {
      try {
        await deleteBag(id);
        toast.success('Bag deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Create bag handler
  const createBagHandler = async () => {
    if (window.confirm('Are you sure you want to create a new note?')) {
      try {
        const newBag = {
          date: new Date().toISOString().split('T')[0], // Default to today
          status: 'Pending',
          accountName: 'Default Account',
          details: 'New Note',
          number: 0,
        };
        await createBag(newBag).unwrap();
        toast.success('New note created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Sorting handler
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const filterAndSortBags = () => {
    let filteredBags = data.bags;

    // Filter by keyword
    if (filter) {
      filteredBags = filteredBags.filter((bag) =>
        bag.status.toLowerCase().includes(filter.toLowerCase()) ||
        bag.accountName.toLowerCase().includes(filter.toLowerCase()) ||
        bag.details.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Sorting based on sortField
    if (sortField) {
      filteredBags.sort((a, b) => {
        if (sortField === 'number') {
          return sortOrder === 'asc'
            ? a.number - b.number
            : b.number - a.number;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          return sortOrder === 'asc'
            ? a[sortField].localeCompare(b[sortField])
            : b[sortField].localeCompare(a[sortField]);
        }
      });
    }

    return filteredBags;
  };

  // NEW: Calculate total bags (sum of number field) for bags within date range.
  // Only include bags if bag.date >= date1 AND bag.date < date2.
  const getTotalFilteredBags = () => {
    if (!data || !data.bags) return 0;
    const filteredBags = data.bags.filter((bag) => {
      if (!bag.date) return false;
      const bagDate = new Date(bag.date).toISOString().split('T')[0];
      return bagDate >= date1 && bagDate < date2;
    });
    return filteredBags.reduce((acc, bag) => acc + Number(bag.number || 0), 0);
  };

  // Grand total of all bags (ignoring date filter)
  const totalNumber = data?.bags.reduce((acc, bag) => acc + Number(bag.number || 0), 0) || 0;

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>General Notes</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3" onClick={createBagHandler}>
            <FaPlus /> Post Data
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h5>Grand Total: {totalNumber}</h5>
        </Col>
      </Row>

      {/* NEW: Date range inputs and filtered total bags */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="date1">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="date2">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <h5>
            Total (Filtered): {getTotalFilteredBags()}
          </h5>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by status, account name, or details"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.data.message}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('date')}
                  style={{ cursor: 'pointer' }}
                >
                  DATE {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>STATUS</th>
                <th
                  onClick={() => handleSort('accountName')}
                  style={{ cursor: 'pointer' }}
                >
                  ACCNAME {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('details')}
                  style={{ cursor: 'pointer' }}
                >
                  DETAILS {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('number')}
                  style={{ cursor: 'pointer' }}
                >
                  NUMBER {sortField === 'number' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>IMAGES</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortBags().map((bag) => (
                <tr key={bag._id}>
                  <td>{new Date(bag.date).toLocaleDateString()}</td>
                  <td>{bag.status}</td>
                  <td>{bag.accountName}</td>
                  <td>{bag.details}</td>
                  <td>{bag.number}</td>
                  <td className="thumbnails">
                    {bag.images?.length > 0 && (
                      <img
                        src={bag.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <Link
                      className="btn btn-primary btn-sm m-2"
                      to={`/bag/${bag._id}`}
                      role="button"
                    >
                    Details
                    </Link>
                    <Button
                      as={Link}
                      to={`/bag/${bag._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      style={{ display: 'none' }}
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(bag._id)}
                    >
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Paginate pages={data.pages} page={data.page} isAdmin={true} />
        </>
      )}
    </>
  );
};

export default StaffNote;